import { Link, useNavigation, useSearchParams } from '@remix-run/react';
import {
    type ProfessionalTileProps,
    type AvatarProps,
    useStyles,
} from '@archipro-design/aria';
import React, { useState } from 'react';
import { getProfessionalTile } from '@modules/professionals-category/util/get-professional-tile';
import type { ProfessionalTileViewModel } from '~/modules/professionals-category/type';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import FeaturedItemsMobile from '~/modules/detail/components/featured-items/FeaturedItems.mobile';
import * as S from './FeaturedProfessionals.mobile.style';

interface FeaturedProfessionalsData {
    Title: string;
    FeaturedProfessionals: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProfessionalTileViewModel;
        }[];
    };
}

interface FeaturedProfessionalsProps {
    data: FeaturedProfessionalsData;
}

const ROW_SIZE = 3;
const DEFAULT_TILE_LIMIT = ROW_SIZE;
const SHOW_ALL_PARAM = 'featured-professionals-show-all';

const FeaturedProfessionals = ({
    data: { FeaturedProfessionals },
}: FeaturedProfessionalsProps) => {
    const [searchParams] = useSearchParams();
    const [showing, setShowing] = useState(false);
    const navigation = useNavigation();
    const { css } = useStyles();

    const { edges, pageInfo } = FeaturedProfessionals;
    const { favouriteItem } = useSaveToDesignBoard('ProfessionalPin');

    if (!edges?.length) return null;

    const totalCount = pageInfo.totalCount ?? 0;

    const showingAll = !!searchParams.get(SHOW_ALL_PARAM) || showing;
    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const featuredShowAll = nextSearchParams.get(SHOW_ALL_PARAM) === '1';
    const isLoading = navigation.state === 'loading' && featuredShowAll;

    return (
        <div className={css(S.ProfessionalsWrapper)}>
            <FeaturedItemsMobile
                gridType="single"
                title={
                    <>
                        Professionals used <br /> in this project
                    </>
                }
                button={{
                    as: Link,
                    to: `?${SHOW_ALL_PARAM}=1`,
                    onClick: () => {
                        if (totalCount <= DEFAULT_TILE_LIMIT) {
                            setShowing(true);
                        }
                    },
                    loading: isLoading,
                    children: !isLoading ? `View more` : 'Loading...',
                }}
                displayButton={!showingAll && totalCount > ROW_SIZE}
            >
                {edges
                    .slice(0, showingAll ? undefined : DEFAULT_TILE_LIMIT)
                    .map(({ node: professional }) => {
                        const final = getProfessionalTile(
                            professional,
                            (link) => ({
                                as: Link,
                                to: link,
                            })
                        ) as ProfessionalTileProps;
                        return (
                            <a
                                key={`featured-professional-tile-${professional.ID}`}
                                href={professional.link}
                                className={css(S.ProfessionalTileLink)}
                            >
                                <ProfessionalTile
                                    title={final.title}
                                    image={[
                                        {
                                            src: professional.image || '',
                                            alt:
                                                typeof final === 'object'
                                                    ? (final.title as string)
                                                    : '',
                                        },
                                    ]}
                                    logo={{
                                        image: {
                                            src:
                                                typeof final.logo === 'object'
                                                    ? ((
                                                          final.logo as unknown as AvatarProps
                                                      ).image as string)
                                                    : '',
                                            alt: `${final.title} professional logo`,
                                            loading: 'lazy',
                                        },
                                        ...(typeof final.logo === 'object' && {
                                            variables: (
                                                final.logo as unknown as AvatarProps
                                            ).variables,
                                        }),
                                    }}
                                    category={final.category}
                                    memberSince={final.memberSince}
                                    size={'small'}
                                    variant="04"
                                    isFavourited={favouriteItem.favourited(
                                        professional.ID
                                    )}
                                    onFavourite={(e) =>
                                        favouriteItem.onClick(
                                            e,
                                            professional.ID
                                        )
                                    }
                                    services={final.services}
                                    impression={{
                                        type: 'LibraryItem_Directory',
                                        itemID: professional.ID,
                                        professionalID: professional.ID,
                                    }}
                                />
                            </a>
                        );
                    })}
            </FeaturedItemsMobile>
            {favouriteItem.target && <SaveToDesignBoard {...favouriteItem} />}
        </div>
    );
};

export default FeaturedProfessionals;
